import React, { useState, useEffect } from "react";
import { useAuth } from "../auth/AuthContext";
import { provisionWallet, getWalletBalances } from "../lib/investApi";
import InvestCollectionsTab from "../components/invest/InvestCollectionsTab";
import InvestPortfolioTab from "../components/invest/InvestPortfolioTab";
import InvestOrdersTab from "../components/invest/InvestOrdersTab";
import InvestWithdrawTab from "../components/invest/InvestWithdrawTab";
import { TrendingUp, Wallet, ClipboardList, ArrowUpRight, Shield, BarChart3, Coins, Lock } from "lucide-react";
import { Link } from "react-router-dom";

const TABS = [
  { id: "collections", label: "Explore Collections", icon: BarChart3 },
  { id: "portfolio",   label: "My Portfolio",        icon: Wallet },
  { id: "orders",      label: "Order History",       icon: ClipboardList },
  { id: "withdraw",    label: "Withdraw",             icon: ArrowUpRight },
];

export default function Investment() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("collections");
  const [walletUserId, setWalletUserId] = useState(null);
  const [holdings, setHoldings] = useState([]);
  const [portfolioRefreshKey, setPortfolioRefreshKey] = useState(0);
  const [provisioning, setProvisioning] = useState(false);
  const [provisionError, setProvisionError] = useState(null);

  useEffect(() => {
    if (!user) return;
    setProvisioning(true);
    setProvisionError(null);
    const uid = user.user_id;
    provisionWallet(uid)
      .then(({ walletUserId: wid }) => {
        setWalletUserId(wid);
        return getWalletBalances(wid);
      })
      .then(({ tokens }) => setHoldings(tokens))
      .catch((e) => setProvisionError(e.message))
      .finally(() => setProvisioning(false));
  }, [user]);

  const handlePurchaseComplete = () => {
    setPortfolioRefreshKey(k => k + 1);
    if (walletUserId) {
      getWalletBalances(walletUserId).then(({ tokens }) => setHoldings(tokens)).catch(() => {});
    }
  };

  return (
    <div className="min-h-screen" style={{ paddingTop: 64 }}>
      {/* Hero */}
      <section style={{
        background: "linear-gradient(135deg, rgba(225,29,72,0.07) 0%, rgba(37,99,235,0.11) 100%)",
        borderBottom: "1px solid var(--zc-border)",
        padding: "52px 0 40px",
      }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
            <span className="eyebrow" style={{ color: "var(--zc-secondary)", fontSize: 11 }}>BLOCKCHAIN INVESTMENT · TESTNET</span>
          </div>
          <h1 className="zc-headline" style={{ fontSize: "clamp(26px,4vw,46px)", lineHeight: 1.2, marginBottom: 16, maxWidth: 640 }}>
            Own fractions of{" "}
            <span style={{ background: "var(--zc-gradient)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
              premium cars
            </span>
          </h1>
          <p style={{ color: "var(--zc-text-2)", maxWidth: 520, lineHeight: 1.65, marginBottom: 32, fontSize: 15 }}>
            Buy tokenized shares of curated car collections on a local blockchain.
            Earn from rental revenue and withdraw your tokens anytime.
          </p>

          <div style={{ display: "flex", gap: 28, flexWrap: "wrap" }}>
            {[
              { label: "Asset-backed tokens",         icon: Shield },
              { label: "Local blockchain (Hardhat)",  icon: Coins },
              { label: "Instant settlement",          icon: TrendingUp },
            ].map(({ label, icon: Icon }) => (
              <div key={label} style={{ display: "flex", alignItems: "center", gap: 7, color: "var(--zc-text-2)", fontSize: 13 }}>
                <Icon size={15} style={{ color: "var(--zc-secondary)", flexShrink: 0 }} />
                {label}
              </div>
            ))}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 16, marginTop: 28, flexWrap: "wrap" }}>
            <button
              onClick={() => setActiveTab("collections")}
              className="zc-btn-primary"
              style={{ padding: "11px 28px", fontSize: 14, fontWeight: 600 }}
            >
              Start Investing
            </button>
            <div style={{ display: "flex", gap: 24 }}>
              {[
                { value: "5", label: "Token types" },
                { value: "$80", label: "Entry price" },
                { value: "100%", label: "On-chain" },
              ].map(({ value, label }) => (
                <div key={label} style={{ textAlign: "center" }}>
                  <div style={{ fontWeight: 700, fontSize: 18, color: "var(--zc-text-1)" }}>{value}</div>
                  <div style={{ fontSize: 11, color: "var(--zc-text-3)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Sticky tab bar */}
      <div style={{
        borderBottom: "1px solid var(--zc-border)",
        background: "var(--zc-bg-2)",
        position: "sticky", top: 64, zIndex: 30,
      }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px", display: "flex", overflowX: "auto" }}>
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              style={{
                display: "flex", alignItems: "center", gap: 7,
                padding: "13px 18px", fontSize: 13, fontWeight: 500,
                border: "none", background: "none", cursor: "pointer",
                borderBottom: activeTab === id
                  ? "2px solid var(--zc-secondary)"
                  : "2px solid transparent",
                color: activeTab === id ? "var(--zc-secondary)" : "var(--zc-text-2)",
                transition: "color 0.15s, border-color 0.15s",
                whiteSpace: "nowrap",
              }}
              data-testid={`invest-tab-${id}`}
            >
              <Icon size={14} />
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "32px 24px" }}>
        {!user ? (
          <div style={{ textAlign: "center", padding: "80px 0" }}>
            <Lock size={44} style={{ color: "var(--zc-text-3)", margin: "0 auto 16px", display: "block" }} />
            <p className="zc-headline" style={{ fontSize: 20, marginBottom: 8 }}>Sign in to invest</p>
            <p style={{ color: "var(--zc-text-2)", marginBottom: 24, fontSize: 14 }}>
              Create an account to buy car collection tokens and manage your portfolio.
            </p>
            <Link to="/login" className="zc-btn-primary" style={{ padding: "10px 24px", fontSize: 14 }}>
              Sign in
            </Link>
          </div>
        ) : provisioning ? (
          <div style={{ textAlign: "center", padding: "80px 0", color: "var(--zc-text-2)", fontSize: 14 }}>
            <div style={{ width: 32, height: 32, border: "3px solid var(--zc-secondary)", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 16px" }} />
            Setting up your investment wallet…
          </div>
        ) : provisionError ? (
          <div style={{ padding: 16, background: "#FEF2F2", borderRadius: 8, color: "var(--zc-danger)", fontSize: 14, maxWidth: 500, margin: "40px auto" }}>
            <strong>Could not connect to investment services.</strong><br />
            {provisionError}<br />
            <span style={{ fontSize: 12, color: "var(--zc-text-3)", marginTop: 6, display: "block" }}>
              Make sure the wallet-service and chain-service are running.
            </span>
          </div>
        ) : (
          <>
            {activeTab === "collections" && (
              <InvestCollectionsTab
                walletUserId={walletUserId}
                onPurchaseComplete={handlePurchaseComplete}
              />
            )}
            {activeTab === "portfolio" && (
              <InvestPortfolioTab
                walletUserId={walletUserId}
                refreshKey={portfolioRefreshKey}
              />
            )}
            {activeTab === "orders" && (
              <InvestOrdersTab walletUserId={walletUserId} />
            )}
            {activeTab === "withdraw" && (
              <InvestWithdrawTab
                walletUserId={walletUserId}
                holdings={holdings}
                onWithdrawComplete={() => setPortfolioRefreshKey(k => k + 1)}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
